import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Check, Minus, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { CARD_SURFACE, categorySurfaceStyle } from '@/lib/menuCategoryStyle'
import { resolveLineUnitPrice, type ModifierOptionPrice } from '@/lib/lineModifiers'
import {
  defaultSelections,
  isOneTapGroup,
  isSingleChoice,
  orderedOptions,
  requirementLabel,
  selectionSummary,
  selectionsToWire,
  unitsInGroup,
  validateSelections,
  wireToSelections,
  type SelectionMap,
} from '@/lib/modifierRules'
import type { ModifierGroup, ModifierOption } from '@/types'

/**
 * The options sheet the counter opens when a configured product is tapped
 * (F-MOD-N7).
 *
 * Every group the product carries — size, required picks, paid add-ons — in
 * ONE sheet with one confirm, because a cashier mid-rush should answer a
 * question once and move on, not walk a wizard.
 *
 * Imports nothing counter-specific, so the floor tablet reuses it verbatim.
 *
 * Two deliberate interaction choices:
 *
 *  - Confirm is NEVER disabled. Tapping it with something unanswered scrolls
 *    the first offending group into view and marks it. A disabled button on a
 *    touch POS is a dead end: the cashier taps, nothing happens, they tap
 *    harder, then they call a manager.
 *
 *  - Sold-out options stay VISIBLE, struck through. Hiding one makes the
 *    cashier assume it was deleted and go looking for someone to fix the menu.
 *
 * And three for speed (F-FE-N182):
 *
 *  - One tap. When the only question is one required single choice (a sized
 *    item), picking the option adds the line — there is nothing left to
 *    confirm. Editing an existing line still waits for Save.
 *
 *  - Keys. 1–9 pick in the active group (marked by a rule on its left), the
 *    sheet moves on to the next unanswered group by itself, ↑/↓ move between
 *    groups, Enter adds. A counter with a keyboard never needs the mouse.
 *
 *  - The footer reads back what will go on the ticket ("Medium · Extra
 *    cheese ×2"), and the Add button only quotes a price once the line has
 *    one. Before a size is chosen the "price" is just the base price, which
 *    reads as if a size had been picked for them.
 *
 * And the look (F-FE-N183):
 *
 *  - The sheet wears the tapped tile's category colour: the same left edge
 *    and wash in the header, and on the chosen options, so it reads as that
 *    item rather than a generic dialog. The colour only ever marks borders,
 *    fills and the radio dot; text stays on the foreground token, because
 *    some category colours are light enough that text on them would be
 *    unreadable. Where `color-mix()` is unsupported (older Android WebViews,
 *    see menuCategoryStyle) the tint drops but the accent border still marks
 *    the pick, so nothing becomes illegible.
 *
 *  - The quantity stepper sits in the CENTRE of the footer between Cancel and
 *    Add, with 44px targets. Pinned bottom-left it was out of reach of the
 *    thumb that taps the options. On a sized item, where tapping a size adds
 *    the line at once, the quantity has to be set before the tap, so the
 *    header says how many the tap will add.
 */

export interface ConfiguredLine {
  selections: Array<{ group_id: string; option_id: string; quantity: number }>
  quantity: number
}

export interface ModifierConfiguratorProps {
  open: boolean
  productName: string
  basePrice: number
  groups: ModifierGroup[]
  optionById: ReadonlyMap<string, ModifierOption>
  priceIndex: ReadonlyMap<string, ModifierOptionPrice>
  formatCurrency: (n: number) => string
  /**
   * The product's category accent (`categoryColor`, hex or hsl): the colour
   * of the tile that was tapped. Optional; without one the sheet uses the
   * primary token.
   */
  accent?: string
  /** Prefill when editing an existing line; null starts from the defaults. */
  initial?: ConfiguredLine | null
  onCancel: () => void
  onConfirm: (line: ConfiguredLine) => void
}

export function ModifierConfigurator({
  open,
  productName,
  basePrice,
  groups,
  optionById,
  priceIndex,
  formatCurrency,
  accent: accentProp,
  initial,
  onCancel,
  onConfirm,
}: ModifierConfiguratorProps) {
  const accent = accentProp?.trim() || 'hsl(var(--primary))'
  // The chosen-option tint. Cosmetic only (see the header on color-mix).
  const pickedTint = `color-mix(in srgb, ${accent} 12%, ${CARD_SURFACE})`
  const [sel, setSel] = useState<SelectionMap>({})
  const [qty, setQty] = useState(1)
  const [attempted, setAttempted] = useState(false)
  // Where the number keys act. `cursor` is a group the cashier is still
  // working in (moved to with ↑/↓, or ticking add-ons); a single-choice pick
  // clears it, which hands the keys on to the next unanswered group.
  // `lastPicked` keeps them in place once every group is answered.
  const [cursor, setCursor] = useState<string | null>(null)
  const [lastPicked, setLastPicked] = useState<string | null>(null)
  const groupRefs = useRef<Record<string, HTMLElement | null>>({})

  // Reset on each open so a previous line's choices never leak into the next.
  useEffect(() => {
    if (!open) return
    setSel(initial ? wireToSelections(initial.selections) : defaultSelections(groups))
    setQty(initial?.quantity ?? 1)
    setAttempted(false)
    setCursor(null)
    setLastPicked(null)
    groupRefs.current = {}
  }, [open, initial, groups])

  const validation = useMemo(
    () => validateSelections(groups, optionById, sel),
    [groups, optionById, sel],
  )
  const byGroup = useMemo(
    () => new Map(validation.map((v) => [v.groupId, v])),
    [validation],
  )
  const allSatisfied = validation.every((v) => v.ok)
  const firstBad = validation.find((v) => !v.ok)

  const oneTap = !initial && isOneTapGroup(groups)
  // "tap a size", not "tap a choice", when the one question is a size.
  const oneTapNoun = oneTap && groups[0]?.price_mode === 'replace' ? 'size' : 'choice'

  const activeGroupId = cursor ?? firstBad?.groupId ?? lastPicked ?? groups[0]?.id ?? null

  // Follow the keys: when answering a group hands them to the next one, bring
  // it into view so the cashier can see what the number keys now pick.
  useEffect(() => {
    if (!open || !activeGroupId || groups.length < 2) return
    groupRefs.current[activeGroupId]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  }, [open, activeGroupId, groups.length])

  const unitPrice = useMemo(
    () => resolveLineUnitPrice(basePrice, selectionsToWire(sel), priceIndex),
    [basePrice, sel, priceIndex],
  )

  // "Rs 200 – 500" for a sized item, so the cashier can quote before choosing.
  const priceHint = useMemo(() => {
    const sized = groups.find((g) => g.price_mode === 'replace')
    const prices = (sized?.options ?? []).filter((o) => o.is_available).map((o) => o.price_delta)
    if (prices.length === 0) return formatCurrency(basePrice)
    const lo = Math.min(...prices)
    const hi = Math.max(...prices)
    return lo === hi ? formatCurrency(lo) : `${formatCurrency(lo)} – ${formatCurrency(hi)}`
  }, [groups, basePrice, formatCurrency])

  const summary = selectionSummary(groups, sel)

  const toggle = (g: ModifierGroup, o: ModifierOption) => {
    if (!o.is_available) return
    setLastPicked(g.id)
    setCursor(isSingleChoice(g) ? null : g.id)

    if (oneTap) {
      onConfirm({ selections: [{ group_id: g.id, option_id: o.id, quantity: 1 }], quantity: qty })
      return
    }

    setSel((prev) => {
      const current = prev[g.id] ?? {}
      const has = (current[o.id] ?? 0) > 0

      // Single-choice (and every size group): the pick REPLACES whatever was
      // there, rather than adding a second one the operator must then undo.
      if (isSingleChoice(g)) {
        // Tapping the chosen option again clears it, unless the group is
        // required — silently emptying a required group would just re-block
        // the confirm they are trying to reach.
        if (has && g.min_select === 0) return { ...prev, [g.id]: {} }
        return { ...prev, [g.id]: { [o.id]: 1 } }
      }

      if (has) {
        const next = { ...current }
        delete next[o.id]
        return { ...prev, [g.id]: next }
      }
      const bounds = byGroup.get(g.id)?.bounds
      // At the cap, the next tap swaps rather than being ignored — an
      // unresponsive tile reads as a broken screen.
      if (bounds?.max != null && unitsInGroup(prev, g.id) >= bounds.max) {
        const oldest = Object.keys(current)[0]
        const next = { ...current }
        if (oldest) delete next[oldest]
        return { ...prev, [g.id]: { ...next, [o.id]: 1 } }
      }
      return { ...prev, [g.id]: { ...current, [o.id]: 1 } }
    })
  }

  const bumpOption = (g: ModifierGroup, o: ModifierOption, delta: 1 | -1) => {
    setCursor(g.id)
    setSel((prev) => {
      const current = prev[g.id] ?? {}
      const now = current[o.id] ?? 0
      const next = Math.max(0, Math.min(g.max_per_option, now + delta))
      const updated = { ...current }
      if (next === 0) delete updated[o.id]
      else updated[o.id] = next
      return { ...prev, [g.id]: updated }
    })
  }

  const handleConfirm = () => {
    if (!allSatisfied) {
      setAttempted(true)
      if (firstBad) {
        groupRefs.current[firstBad.groupId]?.scrollIntoView({ block: 'center', behavior: 'smooth' })
      }
      return
    }
    onConfirm({ selections: selectionsToWire(sel), quantity: qty })
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Typing into a field (none today, but a note box is the obvious next
    // thing) must not pick options.
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return
    if (e.metaKey || e.ctrlKey || e.altKey) return

    // Enter confirms. The counter's own hotkeys are suspended while this is
    // open, or Enter would fire the whole cart from inside the sheet. Left to
    // the browser when a button has focus, so Enter on Cancel still cancels.
    if (e.key === 'Enter' && !e.shiftKey && !(e.target instanceof HTMLButtonElement)) {
      e.preventDefault()
      handleConfirm()
      return
    }

    const active = groups.find((g) => g.id === activeGroupId)
    if (/^[1-9]$/.test(e.key) && active) {
      const o = orderedOptions(active)[Number(e.key) - 1]
      if (o) {
        e.preventDefault()
        toggle(active, o)
      }
      return
    }

    if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && groups.length > 1) {
      e.preventDefault()
      const i = groups.findIndex((g) => g.id === activeGroupId)
      const next = groups[(i + (e.key === 'ArrowDown' ? 1 : -1) + groups.length) % groups.length]
      setCursor(next.id)
    }
  }

  if (!open) return null

  // One short group fits a narrow sheet; a full build-your-own needs the room.
  const optionCount = groups.reduce((n, g) => n + (g.options?.length ?? 0), 0)
  const compact = groups.length === 1 && optionCount <= 4

  return (
    <Dialog open onOpenChange={(o) => !o && onCancel()}>
      <DialogContent
        className={cn(
          // outline-none: the sheet itself holds focus (see onOpenAutoFocus),
          // and a ring round the whole dialog is noise. Options keep theirs.
          'flex max-h-[92vh] flex-col gap-0 overflow-hidden p-0 outline-none',
          compact ? 'sm:max-w-lg' : 'sm:max-w-2xl',
        )}
        // A stray tap outside must not bin a half-built line. On a touch POS
        // the sheet covers a grid of product tiles the cashier is reaching
        // across, and losing three chosen scoops to a brushed thumb — with no
        // undo and no message — is the kind of thing that gets a feature
        // switched off. Cancel, the X and Esc all still close it, so there is
        // no way to get stuck.
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        // Focus the sheet, not its first option: the default put a focus ring
        // on the cheapest size the moment it opened, which reads as if that
        // size were already chosen. The sheet holding focus keeps the number
        // keys and Enter working; Tab still walks the options.
        onOpenAutoFocus={(e) => {
          e.preventDefault()
          ;(e.currentTarget as HTMLElement | null)?.focus()
        }}
        onKeyDown={handleKeyDown}
      >
        {/* Same left edge and wash as the product tile that opened the sheet. */}
        <DialogHeader
          className="space-y-1 border-b border-l-4 px-5 py-4 pr-12 text-left"
          style={{ borderLeftColor: accent, ...categorySurfaceStyle(accent) }}
        >
          <DialogTitle className="text-xl leading-tight">{productName}</DialogTitle>
          <DialogDescription className="tabular-nums text-foreground/70">
            {oneTap
              ? `${priceHint} · tap a ${oneTapNoun} to add ${qty === 1 ? 'it' : qty}`
              : priceHint}
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-4">
          {groups.map((g) => {
            const v = byGroup.get(g.id)
            const showError = Boolean(attempted && v && !v.ok)
            const answered = Boolean(v?.ok && v.units > 0)
            const isActive = g.id === activeGroupId
            const required = (v?.bounds.min ?? g.min_select) >= 1
            const chosen = sel[g.id] ?? {}
            const options = orderedOptions(g)
            const noneAvailable = options.every((o) => !o.is_available)
            const single = isSingleChoice(g)
            // Short single-choice groups (sizes) read as one row, left to right.
            const asRow = single && options.length >= 2 && options.length <= 4
            // "No charge" under every sugar level is noise; it only tells the
            // cashier something when other options in the group cost extra.
            const allFree = g.price_mode === 'add' && options.every((o) => o.price_delta <= 0)
            const headingId = `modgroup-${g.id}`
            return (
              <section
                key={g.id}
                ref={(el) => {
                  groupRefs.current[g.id] = el
                }}
                aria-labelledby={headingId}
                // The left rule is the cursor: it marks the group the number
                // keys are picking in, and turns red on a group that blocks Add.
                className={cn(
                  '-ml-3 border-l-2 pl-3 transition-colors',
                  showError ? 'border-destructive' : 'border-transparent',
                )}
                style={!showError && isActive && groups.length > 1 ? { borderLeftColor: accent } : undefined}
              >
                <div className="mb-2 flex items-baseline justify-between gap-3">
                  <h3 id={headingId} className="text-sm font-semibold">
                    {g.prompt || g.name}
                  </h3>
                  <span
                    className={cn(
                      'flex shrink-0 items-center gap-1 text-xs',
                      showError
                        ? 'font-medium text-destructive'
                        : answered && required
                          ? 'text-foreground'
                          : 'text-muted-foreground',
                    )}
                  >
                    {showError ? (
                      v!.message
                    ) : answered && required ? (
                      <>
                        <Check className="h-3.5 w-3.5" style={{ color: accent }} aria-hidden />
                        Done
                      </>
                    ) : required ? (
                      `Required · ${requirementLabel(v?.bounds ?? { min: g.min_select, max: g.max_select }).toLowerCase()}`
                    ) : (
                      requirementLabel(v?.bounds ?? { min: g.min_select, max: g.max_select })
                    )}
                  </span>
                </div>

                {noneAvailable ? (
                  <p className="rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm">
                    Nothing in this group is available, so this item can’t be made right now.
                  </p>
                ) : (
                  <div
                    role={single ? 'radiogroup' : 'group'}
                    aria-labelledby={headingId}
                    className={cn('grid gap-2', !asRow && (g.allow_quantity ? 'grid-cols-2' : 'grid-cols-2 sm:grid-cols-3'))}
                    style={asRow ? { gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` } : undefined}
                  >
                    {options.map((o, oi) => {
                      const count = chosen[o.id] ?? 0
                      const picked = count > 0
                      const price =
                        g.price_mode === 'replace'
                          ? formatCurrency(o.price_delta)
                          : o.price_delta > 0
                            ? `+${formatCurrency(o.price_delta)}`
                            : 'No charge'
                      const showStepper = g.allow_quantity && picked
                      return (
                        <div
                          key={o.id}
                          className={cn(
                            'flex items-stretch rounded-xl border bg-background transition-colors',
                            !picked && 'border-border hover:border-foreground/30',
                            !o.is_available && 'border-dashed bg-muted/40 hover:border-border',
                          )}
                          style={
                            picked
                              ? { borderColor: accent, backgroundColor: pickedTint, boxShadow: `inset 0 0 0 1px ${accent}` }
                              : undefined
                          }
                        >
                          <button
                            type="button"
                            role={single ? 'radio' : 'checkbox'}
                            aria-checked={picked}
                            aria-disabled={!o.is_available}
                            disabled={!o.is_available}
                            onClick={() => toggle(g, o)}
                            className={cn(
                              'flex min-h-[52px] min-w-0 flex-1 items-start gap-2.5 rounded-xl px-3 py-3 text-left',
                              'touch-manipulation outline-none focus-visible:ring-2 focus-visible:ring-ring',
                              'active:scale-[0.98] disabled:cursor-not-allowed disabled:active:scale-100',
                            )}
                          >
                            {/* Radio for one-of, box for any-of: the shape says
                                whether a second tap adds or swaps. */}
                            <span
                              aria-hidden
                              className={cn(
                                'mt-0.5 flex h-[18px] w-[18px] shrink-0 items-center justify-center border-2 transition-colors',
                                single ? 'rounded-full' : 'rounded-[5px]',
                                !picked && 'border-muted-foreground/40',
                              )}
                              style={picked ? { borderColor: accent, backgroundColor: accent } : undefined}
                            >
                              {picked &&
                                (single ? (
                                  <span className="h-1.5 w-1.5 rounded-full bg-card" />
                                ) : (
                                  <Check className="h-3 w-3 text-card" strokeWidth={3} />
                                ))}
                            </span>
                            <span className="min-w-0 flex-1">
                              <span
                                className={cn(
                                  'block text-sm font-medium leading-snug',
                                  !o.is_available && 'text-muted-foreground line-through',
                                )}
                              >
                                {o.name}
                              </span>
                              {(!o.is_available || !allFree) && (
                                <span
                                  className={cn(
                                    'mt-0.5 block tabular-nums',
                                    o.is_available && g.price_mode === 'replace'
                                      ? 'text-base font-semibold text-foreground'
                                      : 'text-xs text-muted-foreground',
                                  )}
                                >
                                  {o.is_available ? price : 'Sold out'}
                                </span>
                              )}
                            </span>
                            {/* Key hints only where there is a keyboard, and only
                                on the group the keys are acting on. */}
                            {isActive && oi < 9 && o.is_available && !showStepper && (
                              <kbd className="hidden h-4 shrink-0 items-center rounded border border-border bg-background px-1 font-mono text-[10px] text-muted-foreground [@media(hover:hover)]:inline-flex">
                                {oi + 1}
                              </kbd>
                            )}
                          </button>

                          {/* Per-option repeats — "Extra Shot x2" on one line. */}
                          {showStepper && (
                            <div className="flex shrink-0 items-center gap-0.5 pr-1.5">
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-7 w-7"
                                aria-label={`One less ${o.name}`}
                                onClick={() => bumpOption(g, o, -1)}
                              >
                                <Minus className="h-3 w-3" />
                              </Button>
                              <span className="min-w-[1.5rem] text-center text-xs font-semibold tabular-nums">×{count}</span>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-7 w-7"
                                aria-label={`One more ${o.name}`}
                                disabled={count >= g.max_per_option}
                                onClick={() => bumpOption(g, o, 1)}
                              >
                                <Plus className="h-3 w-3" />
                              </Button>
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )}
              </section>
            )
          })}
        </div>

        <div className="border-t bg-muted/30 px-5 pb-4 pt-3">
          {/* The ticket line, read back before it is added. */}
          {!oneTap && (
            <p className="mb-3 truncate text-center text-sm" aria-live="polite">
              <span className="tabular-nums text-muted-foreground">{qty} ×</span>{' '}
              <span className="font-medium">{productName}</span>
              {summary ? <span className="text-muted-foreground"> · {summary}</span> : null}
            </p>
          )}

          {/*
            Cancel, quantity, Add, with the stepper in the middle where the
            thumb already is. On a phone the stepper takes its own centred row
            above the two buttons.
          */}
          <div className="grid grid-cols-2 items-center gap-3 sm:grid-cols-[1fr_auto_1fr]">
            <div
              className="col-span-2 flex items-center justify-center gap-3 sm:order-2 sm:col-span-1"
              role="group"
              aria-label="Quantity"
            >
              <Button
                size="icon"
                variant="outline"
                className="h-11 w-11 rounded-full"
                aria-label="One fewer"
                disabled={qty <= 1}
                onClick={() => setQty((q) => Math.max(1, q - 1))}
              >
                <Minus className="h-5 w-5" />
              </Button>
              <span
                className="min-w-[2.5rem] text-center text-2xl font-semibold tabular-nums"
                aria-live="polite"
                aria-label={`Quantity ${qty}`}
              >
                {qty}
              </span>
              <Button
                size="icon"
                variant="outline"
                className="h-11 w-11 rounded-full"
                aria-label="One more"
                onClick={() => setQty((q) => q + 1)}
              >
                <Plus className="h-5 w-5" />
              </Button>
            </div>

            <Button
              variant="outline"
              className={cn('h-11 sm:order-1 sm:justify-self-start sm:px-6', oneTap && 'col-span-2 sm:col-span-1')}
              onClick={onCancel}
            >
              Cancel
            </Button>

            {oneTap ? (
              // Nothing to confirm: the size tap adds the line. This keeps the
              // grid balanced so the stepper stays centred.
              <span className="hidden sm:order-3 sm:block" aria-hidden />
            ) : (
              <Button
                onClick={handleConfirm}
                // Never disabled (see the header): it reads as unfinished
                // instead, and a tap still takes them to what is missing.
                variant={allSatisfied ? 'default' : 'secondary'}
                className="h-11 justify-between gap-3 sm:order-3 sm:min-w-[11rem] sm:justify-self-end"
              >
                {allSatisfied ? (
                  <>
                    <span>{initial ? 'Save' : 'Add'}</span>
                    <span className="tabular-nums">{formatCurrency(unitPrice * qty)}</span>
                  </>
                ) : (
                  <span className="w-full truncate text-center">{firstBad?.message || 'Finish choosing'}</span>
                )}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
